import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Home, Filter, Users, Layers, Plug, TestTube,
  Settings, Bot, History, ChartLine, LogOut, User, Server,
  Radar,
  LucideLoader,
  LucideFileQuestion,
  AlertCircle,
  MessageCircleCodeIcon,
  FlaskConical,
} from "lucide-react";
import { useAuth } from "./features/AuthContext";
import { useToast } from "../hooks/useToast"


interface SidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function Sidebar({ open, onOpenChange }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate()
  const { user, logout ,authorized} = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleLogout = () => {
  logout();
  if(!authorized){
    navigate("/login")
  }
  queryClient.clear();
  toast({
    title: "Success",
    description: "Logged out successfully",
  });
};

  const menuItems = [
    { to: "/", icon: Home, label: "Dashboard" },
    ...(user?.role === "admin"
      ? [
          { to: "/filters", icon: Filter, label: "Filters", admin: true },
          { to: "/users", icon: Users, label: "Users", admin: true },
          { to: "/services", icon: Server, label: "Services", admin: true }
        ]
      : []),
    { to: "/buckets", icon: Layers, label: "Buckets" },
    { to: "/leader-integration", icon: LucideLoader, label: "Leader Settings" },
      { to: "/liquidation-alert", icon:MessageCircleCodeIcon , label: "Alerts" },
    { to: "/platform-integration", icon: Plug, label: "Platform Integration" },
    { to: "/manual-testing", icon: TestTube, label: "Manual Scaning" },
    { to: "/backtesting", icon: FlaskConical, label: "Backtesting" },
    { to: "/order-settings", icon: Settings, label: "Order Settings" },
    { to: "/auto-trade", icon: Bot, label: "Auto Trade" },
    { to: "/order-history", icon: History, label: "Order History" },
  ];
  
  return (
    <div
      className={`w-64 bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out lg:translate-x-0 transform ${
        open ? "translate-x-0" : "-translate-x-full"
      } fixed lg:relative z-30 h-full`}
    >
      {/* Header */}
      <div className="p-[18px] border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <ChartLine className="w-4 h-4 text-black" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">WaliaChandan</h1>
            <p className="text-xs text-gray-500">
              {user?.role === "admin" ? "Admin Panel" : "Trading Panel"}
            </p>
          </div>
        </div>
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center space-x-2">
          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-black" />
          </div>
          <div>
            <p className="text-xs text-gray-500">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Nav Menu */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {menuItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => onOpenChange(false)}
            className={({ isActive }) =>
              `flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                isActive ? "bg-secondary text-gray-500" : "text-gray-700 hover:bg-gray-100"
              }`
            }
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
            {item.admin && (
              <span className="bg-warning text-grey-600 text-xs px-2 py-1 rounded-full ml-auto">
                Admin
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200">
        <button
          onClick={handleLogout}
          className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
}